import {
  type BucketMap,
  type BucketOverrides,
  type BucketTotals,
  type ILedgerLine,
  ReportingBucket,
} from '../types/buckets';

interface IBucketRule {
  test: RegExp;
  bucket: ReportingBucket;
}

export const OPEX_BUCKETS: ReportingBucket[] = [
  ReportingBucket.OPEX_PAYROLL,
  ReportingBucket.OPEX_RENT_FACILITIES,
  ReportingBucket.OPEX_PROFESSIONAL_SERVICES,
  ReportingBucket.OPEX_SOFTWARE,
  ReportingBucket.OPEX_MARKETING,
  ReportingBucket.OPEX_TRAVEL,
  ReportingBucket.OPEX_OTHER,
  ReportingBucket.DEPRECIATION_AMORTISATION,
];

export const PNL_BUCKETS: ReportingBucket[] = [
  ReportingBucket.REVENUE,
  ReportingBucket.COST_OF_GOODS_SOLD,
  ...OPEX_BUCKETS,
  ReportingBucket.NON_OPERATING_INCOME,
  ReportingBucket.NON_OPERATING_EXPENSE,
  ReportingBucket.INCOME_TAX,
];

export const BALANCE_SHEET_BUCKETS: ReportingBucket[] = [
  ReportingBucket.CASH,
  ReportingBucket.INVESTMENTS,
  ReportingBucket.ACCOUNTS_RECEIVABLE,
  ReportingBucket.INVENTORY,
  ReportingBucket.OTHER_CURRENT_ASSETS,
  ReportingBucket.FIXED_ASSETS,
  ReportingBucket.OTHER_NON_CURRENT_ASSETS,
  ReportingBucket.ACCOUNTS_PAYABLE,
  ReportingBucket.DEBT,
  ReportingBucket.OTHER_CURRENT_LIABILITIES,
  ReportingBucket.OTHER_NON_CURRENT_LIABILITIES,
  ReportingBucket.SHARE_CAPITAL,
  ReportingBucket.RETAINED_EARNINGS,
  ReportingBucket.OTHER_EQUITY,
];

export const BUCKET_LABELS: Record<ReportingBucket, string> = {
  REVENUE: 'Revenue',
  COST_OF_GOODS_SOLD: 'Cost of goods sold',
  OPEX_PAYROLL: 'Payroll',
  OPEX_RENT_FACILITIES: 'Rent & facilities',
  OPEX_PROFESSIONAL_SERVICES: 'Professional services',
  OPEX_SOFTWARE: 'Software & tools',
  OPEX_MARKETING: 'Marketing',
  OPEX_TRAVEL: 'Travel',
  OPEX_OTHER: 'Other opex',
  DEPRECIATION_AMORTISATION: 'Depreciation & amortisation',
  NON_OPERATING_INCOME: 'Non-operating income',
  NON_OPERATING_EXPENSE: 'Non-operating expense',
  INCOME_TAX: 'Income tax',
  CASH: 'Cash',
  INVESTMENTS: 'Investments',
  ACCOUNTS_RECEIVABLE: 'Accounts receivable',
  INVENTORY: 'Inventory',
  OTHER_CURRENT_ASSETS: 'Other current assets',
  FIXED_ASSETS: 'Fixed assets',
  OTHER_NON_CURRENT_ASSETS: 'Other non-current assets',
  ACCOUNTS_PAYABLE: 'Accounts payable',
  DEBT: 'Debt',
  OTHER_CURRENT_LIABILITIES: 'Other current liabilities',
  OTHER_NON_CURRENT_LIABILITIES: 'Other non-current liabilities',
  SHARE_CAPITAL: 'Share capital',
  RETAINED_EARNINGS: 'Retained earnings',
  OTHER_EQUITY: 'Other equity',
  EXCLUDED: 'Excluded',
};

const DEPRECIATION_RULE: IBucketRule = {
  test: /depreciation|amorti[sz]ation/i,
  bucket: ReportingBucket.DEPRECIATION_AMORTISATION,
};

const OPEX_RULES: IBucketRule[] = [
  DEPRECIATION_RULE,
  {
    test: /payroll|salar|wages|bonus|benefit|contractor|staff|employee|recruit|pf\b|esi\b|gratuity/i,
    bucket: ReportingBucket.OPEX_PAYROLL,
  },
  {
    test: /rent|lease|office|utilit|electric|facility|facilities|property/i,
    bucket: ReportingBucket.OPEX_RENT_FACILITIES,
  },
  {
    test: /legal|account(ing|ant)|audit|consult|professional|advisory|tax filing|compliance/i,
    bucket: ReportingBucket.OPEX_PROFESSIONAL_SERVICES,
  },
  {
    test: /software|saas|subscription|hosting|cloud|server|tools|license/i,
    bucket: ReportingBucket.OPEX_SOFTWARE,
  },
  {
    test: /marketing|advertis|promotion|brand|campaign|events?/i,
    bucket: ReportingBucket.OPEX_MARKETING,
  },
  {
    test: /travel|flight|hotel|lodging|meals|conveyance/i,
    bucket: ReportingBucket.OPEX_TRAVEL,
  },
];

const BALANCE_SHEET_RULES: IBucketRule[] = [
  {
    test: /cash|bank|petty|checking|current account|savings/i,
    bucket: ReportingBucket.CASH,
  },
  {
    test: /invest|deposit|treasury|mutual fund|marketable|fd\b/i,
    bucket: ReportingBucket.INVESTMENTS,
  },
  {
    test: /receivable|a\/r|debtors|unbilled/i,
    bucket: ReportingBucket.ACCOUNTS_RECEIVABLE,
  },
  { test: /inventory|stock/i, bucket: ReportingBucket.INVENTORY },
  {
    test: /payable|a\/p|creditors|accrued/i,
    bucket: ReportingBucket.ACCOUNTS_PAYABLE,
  },
  {
    test: /loan|debt|borrow|note payable|credit card|line of credit|convertible/i,
    bucket: ReportingBucket.DEBT,
  },
  {
    test: /fixed asset|property|plant|equipment|furniture|vehicle|computer|ppe|intangible|capital work/i,
    bucket: ReportingBucket.FIXED_ASSETS,
  },
  {
    test: /share capital|common stock|preferred|paid.?in|contributed|opening balance equity|owner/i,
    bucket: ReportingBucket.SHARE_CAPITAL,
  },
  {
    test: /retained|net income|current year earnings|accumulated/i,
    bucket: ReportingBucket.RETAINED_EARNINGS,
  },
];

const INCOME_TAX_PATTERN = /income tax|corporate tax|provision for tax/i;
const CURRENT_PATTERN = /current/i;
const NON_CURRENT_PATTERN = /non.?current|long.?term|fixed/i;

/**
 * Maps ledger lines to reporting buckets. The statement section decides the
 * area; account and parent names refine within it; per-company overrides win.
 */
export class BucketHelper {
  static emptyTotals(): BucketTotals {
    return Object.values(ReportingBucket).reduce<BucketTotals>(
      (totals, bucket) => ({ ...totals, [bucket]: 0 }),
      // Filled in for every enum member by the reduce.
      {} as BucketTotals
    );
  }

  static sum(totals: BucketTotals, keys: ReportingBucket[]): number {
    return keys.reduce((s, k) => s + totals[k], 0);
  }

  static buildMap(
    lines: ILedgerLine[],
    overrides: BucketOverrides = {}
  ): BucketMap {
    return new Map(
      lines.map((line) => [
        line.id,
        overrides[line.id] ?? BucketHelper.defaultBucketFor(line),
      ])
    );
  }

  static defaultBucketFor(line: ILedgerLine): ReportingBucket {
    const names = [line.name, ...[...line.path].reverse()];
    switch (line.section) {
      case 'revenue':
        return ReportingBucket.REVENUE;
      case 'cost_of_goods_sold':
        return ReportingBucket.COST_OF_GOODS_SOLD;
      case 'operating_expenses':
        return (
          BucketHelper.matchFirst(OPEX_RULES, names) ??
          ReportingBucket.OPEX_OTHER
        );
      case 'non_operating_revenue':
        return ReportingBucket.NON_OPERATING_INCOME;
      case 'non_operating_expenses':
        if (INCOME_TAX_PATTERN.test(line.name))
          return ReportingBucket.INCOME_TAX;
        return (
          BucketHelper.matchFirst([DEPRECIATION_RULE], names) ??
          ReportingBucket.NON_OPERATING_EXPENSE
        );
      case 'assets':
        return (
          BucketHelper.matchFirst(BALANCE_SHEET_RULES, names, [
            ReportingBucket.CASH,
            ReportingBucket.INVESTMENTS,
            ReportingBucket.ACCOUNTS_RECEIVABLE,
            ReportingBucket.INVENTORY,
            ReportingBucket.FIXED_ASSETS,
          ]) ??
          (BucketHelper.isCurrent(line.path)
            ? ReportingBucket.OTHER_CURRENT_ASSETS
            : ReportingBucket.OTHER_NON_CURRENT_ASSETS)
        );
      case 'liabilities':
        return (
          BucketHelper.matchFirst(BALANCE_SHEET_RULES, names, [
            ReportingBucket.ACCOUNTS_PAYABLE,
            ReportingBucket.DEBT,
          ]) ??
          (BucketHelper.isCurrent(line.path)
            ? ReportingBucket.OTHER_CURRENT_LIABILITIES
            : ReportingBucket.OTHER_NON_CURRENT_LIABILITIES)
        );
      case 'equity':
        return (
          BucketHelper.matchFirst(BALANCE_SHEET_RULES, names, [
            ReportingBucket.SHARE_CAPITAL,
            ReportingBucket.RETAINED_EARNINGS,
          ]) ?? ReportingBucket.OTHER_EQUITY
        );
      default:
        return ReportingBucket.EXCLUDED;
    }
  }

  /** The account's own name wins over its parent groups; within one name, rule order decides. */
  private static matchFirst(
    rules: IBucketRule[],
    haystacks: string[],
    allowed?: ReportingBucket[]
  ): ReportingBucket | null {
    const candidates = allowed
      ? rules.filter((r) => allowed.includes(r.bucket))
      : rules;
    const hit = haystacks
      .map((haystack) => candidates.find((rule) => rule.test.test(haystack)))
      .find((rule) => rule !== undefined);
    return hit?.bucket ?? null;
  }

  private static isCurrent(path: string[]): boolean {
    return path.some(
      (p) => CURRENT_PATTERN.test(p) && !NON_CURRENT_PATTERN.test(p)
    );
  }
}
